import { useState, useEffect } from 'react';
import axiosInstance from '../axiosConfig';

export function useRecurringInvoices(initialSize = 20) {
    const [invoices, setInvoices] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(initialSize);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [sorting, setSorting] = useState({ key: null, direction: null });
    const [prevSorting, setPrevSorting] = useState({ key: null, direction: null });
    const [filters, setFilters] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchInvoices = (pageToFetch, sort, currentFilters, reset = false) => {
        let url = `/api/invoices/recurring?page=${pageToFetch}&size=${size}&sortProperty=${sort.key}&sortDirection=${sort.direction}`;
        
        if (currentFilters) {
            Object.keys(currentFilters).forEach(key => {
                if (currentFilters[key] !== null && currentFilters[key] !== '') {
                    url += `&${key}=${encodeURIComponent(currentFilters[key])}`;
                }
            });
        }

        setIsLoading(true);
        axiosInstance.get(url)
            .then(response => response.data)
            .then(data => {
                const items = data.content !== undefined ? data.content : data;
                const total = data.totalElements !== undefined ? data.totalElements : items.length;
                const pages = data.totalPages !== undefined ? data.totalPages : 1;
                
                setInvoices(items);
                setTotalElements(total);
                setTotalPages(pages);
            })
            .catch(error => console.error('Error fetching recurring invoices:', error))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        if (prevSorting.key !== sorting.key || prevSorting.direction !== sorting.direction) {
            setInvoices([]);
            setPage(0);
            fetchInvoices(0, sorting, filters, true);
            setPrevSorting(sorting);
        } else {
            fetchInvoices(page, sorting, filters, false);
        }
    }, [page, size, sorting, filters]);

    const createInvoice = async (formData) => {
        setIsLoading(true);
        try {
            await axiosInstance.post('/api/invoices/recurring', formData);
            setInvoices([]);
            setPage(0);
            fetchInvoices(0, sorting, filters, true);
        } catch (error) {
            console.error('Error creating recurring invoice:', error);
            alert('Грешка при създаването на повтаряща се фактура: ' + error.response?.data?.error?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const updateInvoice = async (invoice) => {
        setIsLoading(true);
        try {
            const payload = {
                ...invoice,
                issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
            };
            await axiosInstance.put(`/api/invoices/recurring/${invoice.id}`, payload);
            setInvoices(prevInvoices => prevInvoices.map(inv => inv.id === invoice.id ? payload : inv));
        } catch (error) {
            console.error('Error updating invoice:', error);
            alert('Грешка при обновяването на фактурата: ' + (error.response?.data?.error?.message || error.message));
        } finally {
            setIsLoading(false);
        }
    };

    const deleteInvoice = async (invoice) => {
        setIsLoading(true);
        try {
            await axiosInstance.delete(`/api/invoices/recurring/${invoice.id}`);
            setInvoices(prevInvoices => prevInvoices.filter(inv => inv.id !== invoice.id));
        } catch (error) {
            console.error('Error deleting invoice:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        invoices,
        isLoading,
        sorting,
        setSorting,
        page,
        setPage,
        size,
        setSize,
        totalPages,
        totalElements,
        filters,
        setFilters,
        createInvoice,
        updateInvoice,
        deleteInvoice
    };
}
